<template>
  <div ref="wrapperRef" class="m-svg-wrapper">
    <div 
      class="m-svg-container" 
      :class="svgClasses"
      :style="svgStyle"
      v-html="sanitizedSvg"
      @click="handleClick"
    />

    <div v-if="hint" class="m-svg-hint">
      {{ hint }}
    </div>

    <FieldVerifyErrorOverlay :messages="normalizedErrors" :anchor-el="wrapperRef" />
  </div>
</template>

<script setup lang="ts">
import DOMPurify from 'dompurify'
import FieldVerifyErrorOverlay from '@/components/ui/FieldVerifyErrorOverlay.vue'
import { computed, ref } from 'vue'

interface Props {
  modelValue?: string
  width?: string
  height?: string
  hint?: string
  disabled?: boolean
  errorMessages?: string | string[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  width: '100%',
  height: 'auto',
  hint: '',
  disabled: false,
  errorMessages: () => []
})

const emit = defineEmits<{
  'click': [event: MouseEvent]
}>()

const wrapperRef = ref<HTMLElement | null>(null)

// Sanitize SVG content to prevent XSS
const sanitizedSvg = computed(() => {
  if (!props.modelValue) return ''
  
  const config = {
    USE_PROFILES: { svg: true, svgFilters: true },
    ADD_TAGS: ['svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'g', 'defs', 'use', 'text', 'tspan'],
    ADD_ATTR: ['viewBox', 'xmlns', 'fill', 'stroke', 'stroke-width', 'd', 'cx', 'cy', 'r', 'x', 'y', 'width', 'height', 'transform']
  }
  
  return DOMPurify.sanitize(props.modelValue, config)
})

const svgClasses = computed(() => [
  'm-svg',
  {
    'm-svg--disabled': props.disabled
  }
])

const svgStyle = computed(() => ({
  width: props.width,
  height: props.height
}))

const normalizedErrors = computed(() => {
  if (Array.isArray(props.errorMessages)) return props.errorMessages
  return [props.errorMessages]
})

const handleClick = (event: MouseEvent) => {
  if (!props.disabled) {
    emit('click', event)
  }
}
</script>

<style scoped lang="scss">
.m-svg-wrapper {
  width: 100%;
}

.m-svg-container {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  :deep(svg) {
    max-width: 100%;
    height: auto;
  }
  
  &--disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.m-svg-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
}
</style>

